import React, { useState, useEffect } from "react";
import moment from "moment";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  DumpsterSize,
  dumpsterSizeToText,
  ServiceType,
  WasteType,
} from "../../../models/Order";
import {
  FleetOptimizationInput,
  isDeliveryCanceled,
  isPickupCanceled,
} from "../../../models";
import { createRun } from "../fleetOptimizationSlice";
import { getDeliveries } from "../../Deliveries/deliveriesSlice";
import { getPickups } from "../../Pickups/pickUpsSlice";
import { getDumpYardTemplates } from "../../DumpYards/dumpYardsSlice";
import {
  PenaltySlider,
  snapToStop,
  BALANCE_STOPS,
  COMPACTNESS_STOPS,
  DUMP_FEE_WEIGHT_STOPS,
  TIME_WINDOW_STOPS,
  END_TIME_STOPS,
  OVERTIME_STOPS,
} from "./PenaltySlider";

import "./NewRunConfig.scss";

interface AssignmentRow {
  id: string;
  driverId: string;
  vehicleId: string;
  dumpsterSize: DumpsterSize | "";
  dumpsterFull: boolean;
  startTime: string; // "HH:MM" shift-start override, or "" for the driver's stored start
  endTime: string; // "HH:MM" shift end, or "" for none
}

function makeRow(): AssignmentRow {
  return {
    id: Math.random().toString(36).slice(2),
    driverId: "",
    vehicleId: "",
    dumpsterSize: "",
    dumpsterFull: false,
    startTime: "",
    endTime: "",
  };
}

function validate(rows: AssignmentRow[]): {
  valid: boolean;
  duplicateDriverIds: Set<string>;
  duplicateVehicleIds: Set<string>;
  missingFields: Set<string>;
} {
  const duplicateDriverIds = new Set<string>();
  const duplicateVehicleIds = new Set<string>();
  const missingFields = new Set<string>();

  const driverIdCounts: Record<string, number> = {};
  const vehicleIdCounts: Record<string, number> = {};

  for (const row of rows) {
    if (!row.driverId || !row.vehicleId) {
      missingFields.add(row.id);
    }
    if (row.driverId) {
      driverIdCounts[row.driverId] = (driverIdCounts[row.driverId] || 0) + 1;
    }
    if (row.vehicleId) {
      vehicleIdCounts[row.vehicleId] = (vehicleIdCounts[row.vehicleId] || 0) + 1;
    }
  }

  for (const row of rows) {
    if (row.driverId && driverIdCounts[row.driverId] > 1) {
      duplicateDriverIds.add(row.driverId);
    }
    if (row.vehicleId && vehicleIdCounts[row.vehicleId] > 1) {
      duplicateVehicleIds.add(row.vehicleId);
    }
  }

  const valid =
    rows.length >= 1 &&
    missingFields.size === 0 &&
    duplicateDriverIds.size === 0 &&
    duplicateVehicleIds.size === 0;

  return { valid, duplicateDriverIds, duplicateVehicleIds, missingFields };
}

export function NewRunConfig() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // "Copy inputs" hands us an existing run's input via router state; we hydrate
  // the whole form from it so the user can tweak and re-run.
  const prefill = (location.state as { prefill?: FleetOptimizationInput } | null)
    ?.prefill;

  const dateParam = searchParams.get("date");
  const date = dateParam || prefill?.date || moment().format("YYYY-MM-DD");
  const numToStr = (n?: number): string => (n != null ? String(n) : "");

  const drivers = useAppSelector((state) => state.driversV2.drivers) || [];
  const vehicles = useAppSelector((state) => state.vehicles.vehicles) || [];
  const allDeliveries = useAppSelector((state) => state.deliveries.deliveries);
  const allPickups = useAppSelector((state) => state.pickups.pickups);
  const createAPI = useAppSelector((state) => state.fleetOptimization.createAPI);
  // Forced-dump-yard options are the dump-yard TEMPLATES (the optimizer routes to
  // yards from dump_yard_templates and validates the forced id against those), not
  // the date-scoped /dump_yards instances — which are empty at planning time and
  // carry a different id space. Filter to templates flagged as dump yards.
  const dumpYards = (
    useAppSelector((state) => state.dumpYards.templates) || []
  ).filter((t) => t.isDumpYard);

  const [rows, setRows] = useState<AssignmentRow[]>(() =>
    prefill?.driverAssignments?.length
      ? prefill.driverAssignments.map((a) => ({
          id: Math.random().toString(36).slice(2),
          driverId: a.driverId,
          vehicleId: a.vehicleId,
          dumpsterSize: (a.startingDumpster?.size ?? "") as DumpsterSize | "",
          dumpsterFull: a.startingDumpster?.state === "full",
          startTime: a.startTime ?? "",
          endTime: a.endTime ?? "",
        }))
      : [makeRow()]
  );

  // Load the date's jobs so they can be locked / excluded / topped off.
  useEffect(() => {
    dispatch(getDeliveries(date));
    dispatch(getPickups(date));
    dispatch(getDumpYardTemplates());
  }, [dispatch, date]);

  // Per-job overrides: mode is "auto" (default) | "exclude" | a driverId (lock).
  const [jobMode, setJobMode] = useState<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    prefill?.locks?.forEach((l) => (m[l.jobId] = l.driverId));
    prefill?.exclude?.forEach((id) => (m[id] = "exclude"));
    return m;
  });
  const [topoff, setTopoff] = useState<Record<string, boolean>>(() => {
    const t: Record<string, boolean> = {};
    prefill?.allowTopoffForJobs?.forEach((id) => (t[id] = true));
    return t;
  });
  // Per-pickup waste-type override (pickupId → waste type; "" = use stored type).
  const [pickupWaste, setPickupWaste] = useState<Record<string, string>>(() => {
    const w: Record<string, string> = {};
    prefill?.pickupWasteTypes?.forEach((x) => (w[x.pickupId] = x.wasteType));
    return w;
  });
  // Per-pickup dump-weight override (pickupId → tons string; "" = size-average).
  const [pickupDumpWeight, setPickupDumpWeight] = useState<Record<string, string>>(
    () => {
      const m: Record<string, string> = {};
      prefill?.pickupDumpWeights?.forEach((x) => (m[x.pickupId] = String(x.weight)));
      return m;
    }
  );
  // Per-pickup forced dump yard (pickupId → dumpYardId; "" = cheapest/auto).
  const [pickupDumpYard, setPickupDumpYard] = useState<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    prefill?.pickupDumpYards?.forEach((x) => (m[x.pickupId] = x.dumpYardId));
    return m;
  });
  // Per-job precedence (jobId → predecessor jobId; "" = no dependency). Keyed by the
  // dependent ("after") job — it must be serviced after the chosen predecessor
  // ("before"), and the optimizer pins both to one truck.
  const [mustFollow, setMustFollow] = useState<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    prefill?.precedences?.forEach((p) => (m[p.afterJobId] = p.beforeJobId));
    return m;
  });
  // Same-unit swap (deliveryJobId → pickupJobId; "" = none). Keyed by the redelivery:
  // it must be serviced as the very next customer job after the chosen pickup, so the
  // customer's own box is returned (a dump between them is fine).
  const [swapWith, setSwapWith] = useState<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    prefill?.swaps?.forEach((s) => (m[s.deliveryJobId] = s.pickupJobId));
    return m;
  });
  // Per-job desired service window (jobId → {start,end}; both "HH:MM"). Keyed by the
  // delivery/pickup id so an order's delivery and pickup carry independent windows.
  // A copied run prefills its job-keyed windows. Legacy (pre-spec-024/042) runs stored
  // them by order id; those have no job_id and the normalizer yields "undefined" — skip
  // them rather than key a window off a non-existent job.
  const [timeWindow, setTimeWindow] = useState<
    Record<string, { start: string; end: string }>
  >(() => {
    const w: Record<string, { start: string; end: string }> = {};
    prefill?.timeWindows?.forEach((x) => {
      if (x.jobId && x.jobId !== "undefined")
        w[x.jobId] = { start: x.start, end: x.end };
    });
    return w;
  });

  // Jobs for the date; any delivery can additionally be topped off. Canceled
  // deliveries/pickups are excluded — they aren't serviced, so they don't belong
  // in an optimization run.
  const jobs = [
    ...allDeliveries
      .filter((d) => d.deliveryDate === date && !isDeliveryCanceled(d))
      .map((d) => ({ id: d.id, orderId: d.orderId, type: "delivery" as const, name: d.name, address: d.address, service: d.service })),
    ...allPickups
      .filter((p) => p.pickupDate === date && !isPickupCanceled(p))
      .map((p) => ({ id: p.id, orderId: p.orderId, type: "pickup" as const, name: p.name, address: p.address, service: p.service })),
  ];

  // Optimizer tuning. Soft-penalty knobs are 5-position sliders (see the *_STOPS
  // scales above); a copied run's stored number snaps to the nearest position. Each
  // slider state holds the mapped number. Fresh-run initial positions: compactness=1,
  // balance=250 (tuned), time-window=200 (tuned, keeps the optimizer honest about
  // promised delivery windows), end-time/overtime off.
  //
  // On COPY we must reproduce the run exactly, including a zero ("Loose"/"Ignore"/
  // "Off") position. onSubmit always sends these scalars, but the backend omitempty-
  // drops a 0, so an absent value on a copied run means it was zero — default to 0,
  // not the fresh-run starting position. Only runs created fresh get the tuned default.
  const [compactnessPenalty, setCompactnessPenalty] = useState<number>(() =>
    prefill
      ? snapToStop(prefill.compactnessPenalty ?? 0, COMPACTNESS_STOPS)
      : 1
  );
  const [overtimeAllowed, setOvertimeAllowed] = useState(
    () => !!prefill?.overtimeAllowed
  );
  const [overtimePenalty, setOvertimePenalty] = useState<number>(() =>
    prefill?.overtimePenalty != null
      ? snapToStop(prefill.overtimePenalty, OVERTIME_STOPS)
      : 0
  );
  const [allowCarryover, setAllowCarryover] = useState(
    () => !!prefill?.allowCarryover
  );
  const [carryoverThreshold, setCarryoverThreshold] = useState(() =>
    numToStr(prefill?.carryoverThreshold)
  );
  const [maxCarryovers, setMaxCarryovers] = useState(() =>
    numToStr(prefill?.maxCarryovers)
  );
  const [endTimePenalty, setEndTimePenalty] = useState<number>(() =>
    prefill?.endTimePenalty != null
      ? snapToStop(prefill.endTimePenalty, END_TIME_STOPS)
      : 0
  );
  const [timeWindowPenalty, setTimeWindowPenalty] = useState<number>(() =>
    prefill
      ? snapToStop(prefill.timeWindowPenalty ?? 0, TIME_WINDOW_STOPS)
      : 200
  );
  const [balancePenalty, setBalancePenalty] = useState<number>(() =>
    prefill
      ? snapToStop(prefill.balancePenalty ?? 0, BALANCE_STOPS)
      : 250
  );
  // Dump-fee rank weight: 100% (1.0) by default (current behavior). On copy, restore
  // the run's value (or default to 1.0 if absent — backend omitempty drops 1.0).
  const [dumpFeeRankWeight, setDumpFeeRankWeight] = useState<number>(() =>
    prefill
      ? snapToStop(prefill.dumpFeeRankWeight ?? 1.0, DUMP_FEE_WEIGHT_STOPS)
      : 1.0
  );
  // Fresh runs default maintenance to $0.50/mi; a copied run keeps its own value.
  // Tuned down from 1: maintenance is a not-billed per-mile wear estimate, and a high
  // value made the solver refuse the cheaper-yard detours that lower real (billed)
  // operating cost. 0.5 beats the human on operating while keeping mileage sensible.
  const [maintenancePerMile, setMaintenancePerMile] = useState(() =>
    prefill ? numToStr(prefill.maintenancePerMile) : "0.5"
  );

  const numOrUndef = (s: string): number | undefined => {
    const n = Number(s);
    return s.trim() !== "" && Number.isFinite(n) ? n : undefined;
  };

  const { valid, duplicateDriverIds, duplicateVehicleIds, missingFields } = validate(rows);

  const updateRow = (id: string, patch: Partial<AssignmentRow>) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );
  };

  const addRow = () => setRows((prev) => [...prev, makeRow()]);

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const driverName = (userId: string) => {
    const d = drivers.find(
      (dr: any) => dr.userId === userId || dr.id === userId
    );
    return d?.name ?? `Driver #${userId}`;
  };
  // Drivers a job can be pinned to = the ones assigned above.
  const assignedDrivers = rows
    .filter((r) => r.driverId)
    .map((r) => ({ id: r.driverId, name: driverName(r.driverId) }));
  const assignedIds = new Set(assignedDrivers.map((d) => d.id));

  const onSubmit = async () => {
    if (!valid || createAPI.loading) return;

    // Per-job overrides are built from the override STATE (not by re-filtering the
    // async-loaded `jobs` list) so a copied run keeps them even if the date's
    // deliveries/pickups haven't finished loading at submit time. The UI only ever
    // sets these for valid jobs, and the backend ignores unknown ids.
    // Locks: only pins to a still-assigned driver.
    const locks = Object.entries(jobMode)
      .filter(([, v]) => v && v !== "auto" && v !== "exclude" && assignedIds.has(v))
      .map(([jobId, driverId]) => ({ jobId, driverId }));
    const exclude = Object.entries(jobMode)
      .filter(([, v]) => v === "exclude")
      .map(([jobId]) => jobId);
    // Top-off is offered on every delivery (the checkbox renders on all of them),
    // so the state already contains only eligible ids.
    const allowTopoffForJobs = Object.entries(topoff)
      .filter(([, on]) => on)
      .map(([jobId]) => jobId);
    const pickupWasteTypes = Object.entries(pickupWaste)
      .filter(([, wt]) => wt)
      .map(([pickupId, wasteType]) => ({ pickupId, wasteType }));
    const pickupDumpWeights = Object.entries(pickupDumpWeight)
      .map(([pickupId, s]) => ({ pickupId, weight: numOrUndef(s) }))
      .filter(
        (x): x is { pickupId: string; weight: number } =>
          x.weight != null && x.weight > 0
      );
    const pickupDumpYards = Object.entries(pickupDumpYard)
      .filter(([, v]) => v)
      .map(([pickupId, dumpYardId]) => ({ pickupId, dumpYardId }));
    // Per-job time windows (only jobs with BOTH ends set), keyed by job id.
    const timeWindows = Object.entries(timeWindow)
      .filter(([, w]) => w.start && w.end)
      .map(([jobId, w]) => ({ jobId, start: w.start, end: w.end }));
    // Precedence links: the row's job (the "after"/dependent) must follow the chosen
    // predecessor ("before"). Skip empty and self-references (backend ignores them too).
    const precedences = Object.entries(mustFollow)
      .filter(([afterJobId, beforeJobId]) => beforeJobId && beforeJobId !== afterJobId)
      .map(([afterJobId, beforeJobId]) => ({ afterJobId, beforeJobId }));
    // Same-unit swaps: the redelivery (key) returns the box picked up at the chosen
    // pickup (value). Skip empty and self-references (backend ignores them too).
    const swaps = Object.entries(swapWith)
      .filter(([deliveryJobId, pickupJobId]) => pickupJobId && pickupJobId !== deliveryJobId)
      .map(([deliveryJobId, pickupJobId]) => ({ pickupJobId, deliveryJobId }));

    const input: FleetOptimizationInput = {
      date,
      driverAssignments: rows.map((row) => ({
        driverId: row.driverId,
        vehicleId: row.vehicleId,
        ...(row.dumpsterSize
          ? {
              startingDumpster: {
                size: row.dumpsterSize as DumpsterSize,
                state: row.dumpsterFull ? "full" : "empty",
              },
            }
          : {}),
        ...(row.startTime ? { startTime: row.startTime } : {}),
        ...(row.endTime ? { endTime: row.endTime } : {}),
      })),
      compactnessPenalty,
      ...(overtimeAllowed
        ? { overtimeAllowed: true }
        : overtimePenalty > 0
        ? // Send overtimeAllowed:false WITH the penalty. The backend defaults
          // OvertimeAllowed to true, which suppresses the overtime penalty entirely;
          // without this flag the operator's penalty value is silently ignored. At
          // the "Lenient" position (0) we omit both, so the backend's default
          // (overtime allowed, no penalty) applies — matching the old blank input.
          { overtimeAllowed: false, overtimePenalty }
        : {}),
      ...(allowCarryover
        ? {
            allowCarryover: true,
            ...(numOrUndef(carryoverThreshold) != null
              ? { carryoverThreshold: numOrUndef(carryoverThreshold) }
              : {}),
            ...(numOrUndef(maxCarryovers) != null
              ? { maxCarryovers: numOrUndef(maxCarryovers) }
              : {}),
          }
        : {}),
      endTimePenalty,
      timeWindowPenalty,
      balancePenalty,
      // Omit dumpFeeRankWeight at 1.0 (the default) so we don't clutter the stored
      // input; a copied run that lacks it is treated as 1.0 on re-load (same as fresh).
      ...(dumpFeeRankWeight !== 1.0 ? { dumpFeeRankWeight } : {}),
      ...(locks.length ? { locks } : {}),
      ...(exclude.length ? { exclude } : {}),
      ...(allowTopoffForJobs.length ? { allowTopoffForJobs } : {}),
      ...(pickupWasteTypes.length ? { pickupWasteTypes } : {}),
      ...(timeWindows.length ? { timeWindows } : {}),
      ...(numOrUndef(maintenancePerMile) != null
        ? { maintenancePerMile: numOrUndef(maintenancePerMile) }
        : {}),
      ...(pickupDumpWeights.length ? { pickupDumpWeights } : {}),
      ...(pickupDumpYards.length ? { pickupDumpYards } : {}),
      ...(precedences.length ? { precedences } : {}),
      ...(swaps.length ? { swaps } : {}),
    };

    const result = await dispatch(createRun(input));
    if (createRun.fulfilled.match(result)) {
      // Back to the runs list for this date (the new run shows up there).
      navigate(`/fleet-optimization?date=${date}`);
    } else {
      toast.error("Failed to create run. Please try again.");
    }
  };

  return (
    <div className="new-run-config-component">
      <div className="page-header">
        <div className="title-container">
          <div className="title-text">New Run</div>
          <div className="title-date">{moment(date).format("MMM D, YYYY")}</div>
          {prefill && (
            <span className="title-copied-badge">Copied from a previous run</span>
          )}
        </div>
        <button
          className="back-link"
          onClick={() => navigate("/fleet-optimization")}
        >
          ← Back to runs
        </button>
      </div>

      <div className="config-card">
        <div className="section-title">Driver Assignments</div>

        <div className="assignments-list">
          {rows.map((row, index) => {
            const hasMissingFields = missingFields.has(row.id);
            const hasDupDriver =
              !!row.driverId && duplicateDriverIds.has(row.driverId);
            const hasDupVehicle =
              !!row.vehicleId && duplicateVehicleIds.has(row.vehicleId);

            return (
              <div
                key={row.id}
                className={`assignment-row${hasMissingFields || hasDupDriver || hasDupVehicle ? " row-error" : ""}`}
              >
                <div className="row-number">{index + 1}</div>

                <div className="row-fields">
                  <div className="field-group">
                    <label>Driver</label>
                    <select
                      value={row.driverId}
                      onChange={(e) =>
                        updateRow(row.id, { driverId: e.target.value })
                      }
                      className={hasDupDriver ? "select-error" : ""}
                    >
                      <option value="">Select a driver</option>
                      {drivers.map((d) => (
                        <option key={d.id} value={d.userId}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                    {hasDupDriver && (
                      <div className="field-error">Driver already assigned</div>
                    )}
                  </div>

                  <div className="field-group">
                    <label>Vehicle</label>
                    <select
                      value={row.vehicleId}
                      onChange={(e) =>
                        updateRow(row.id, { vehicleId: e.target.value })
                      }
                      className={hasDupVehicle ? "select-error" : ""}
                    >
                      <option value="">Select a vehicle</option>
                      {vehicles.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                    {hasDupVehicle && (
                      <div className="field-error">Vehicle already assigned</div>
                    )}
                  </div>

                  <div className="field-group">
                    <label>Starting Dumpster (optional)</label>
                    <select
                      value={row.dumpsterSize}
                      onChange={(e) =>
                        updateRow(row.id, {
                          dumpsterSize: e.target.value as DumpsterSize | "",
                          dumpsterFull: false,
                        })
                      }
                    >
                      <option value="">No dumpster</option>
                      {Object.values(DumpsterSize).map((size) => (
                        <option key={size} value={size}>
                          {dumpsterSizeToText[size]}
                        </option>
                      ))}
                    </select>
                    {row.dumpsterSize && (
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={row.dumpsterFull}
                          onChange={(e) =>
                            updateRow(row.id, { dumpsterFull: e.target.checked })
                          }
                        />
                        <span>Dumpster is full</span>
                      </label>
                    )}
                  </div>

                  <div className="field-group">
                    <label>Start time (optional)</label>
                    <input
                      type="time"
                      value={row.startTime}
                      onChange={(e) =>
                        updateRow(row.id, { startTime: e.target.value })
                      }
                    />
                  </div>

                  <div className="field-group">
                    <label>End time (optional)</label>
                    <input
                      type="time"
                      value={row.endTime}
                      onChange={(e) =>
                        updateRow(row.id, { endTime: e.target.value })
                      }
                    />
                  </div>
                </div>

                {rows.length > 1 && (
                  <button
                    className="remove-row-button"
                    onClick={() => removeRow(row.id)}
                    aria-label="Remove row"
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <button className="add-driver-button" onClick={addRow}>
          + Add driver
        </button>
      </div>

      <div className="config-section">
        <div className="section-title">Optimizer settings</div>
        <div className="config-hint">
          Optional — leave blank for the defaults.
        </div>

        <PenaltySlider
          label="Territory tightness"
          stops={COMPACTNESS_STOPS}
          value={compactnessPenalty}
          onChange={setCompactnessPenalty}
          note="Higher = tighter, less-crossing territories (penalty $ / mile of route spread)."
        />

        <PenaltySlider
          label="Dump-fee weight"
          stops={DUMP_FEE_WEIGHT_STOPS}
          value={dumpFeeRankWeight}
          onChange={setDumpFeeRankWeight}
          note="Lower = prefer closer dump yards / fewer miles. Reported costs are always the real amount."
        />

        <div className="config-field">
          <label>Maintenance ($ / mile)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="default"
            value={maintenancePerMile}
            onChange={(e) => setMaintenancePerMile(e.target.value)}
          />
          <span className="config-note">
            Overrides vehicle maintenance cost; weighs mileage against dump fees
            (also affects dump-yard choice). Blank = default; 0 disables.
          </span>
        </div>

        <PenaltySlider
          label="Respect shift end"
          stops={END_TIME_STOPS}
          value={endTimePenalty}
          onChange={setEndTimePenalty}
          note="Higher = harder to run a driver past their end time, set per driver above (penalty $ / hour late)."
        />

        <PenaltySlider
          label="Even driver days"
          stops={BALANCE_STOPS}
          value={balancePenalty}
          onChange={setBalancePenalty}
          note="Fleet-level nudge toward even driver days (higher = more balanced, possibly costlier)."
        />

        <PenaltySlider
          label="Enforce time windows"
          stops={TIME_WINDOW_STOPS}
          value={timeWindowPenalty}
          onChange={setTimeWindowPenalty}
          note="Higher = harder to service a stop outside its order's window, set per job below (penalty $ / hour outside). Ignored when no windows are set."
        />

        <div className="config-field">
          <label className="config-checkbox">
            <input
              type="checkbox"
              checked={overtimeAllowed}
              onChange={(e) => setOvertimeAllowed(e.target.checked)}
            />
            Allow overtime (routes over 8h)
          </label>
          {!overtimeAllowed && (
            <div className="config-subfield">
              <PenaltySlider
                label="Avoid overtime"
                stops={OVERTIME_STOPS}
                value={overtimePenalty}
                onChange={setOvertimePenalty}
                note="Higher = harder to push a driver past 8h (penalty $ / overtime hour)."
              />
            </div>
          )}
        </div>

        <div className="config-field">
          <label className="config-checkbox">
            <input
              type="checkbox"
              checked={allowCarryover}
              onChange={(e) => setAllowCarryover(e.target.checked)}
            />
            Allow carryover (end day carrying a full container)
          </label>
          {allowCarryover && (
            <div className="config-subfield">
              <label>Carryover threshold (miles out of the way)</label>
              <input
                type="number"
                min="0"
                step="1"
                placeholder="0"
                value={carryoverThreshold}
                onChange={(e) => setCarryoverThreshold(e.target.value)}
              />
              <label>Max carryovers (trucks ending full)</label>
              <input
                type="number"
                min="0"
                step="1"
                placeholder="unlimited"
                value={maxCarryovers}
                onChange={(e) => setMaxCarryovers(e.target.value)}
              />
              <span className="config-note">
                Hard fleet-wide cap on trucks ending the day full; the farthest-dump
                trucks keep the slots. Blank / 0 = unlimited.
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="jobs-section">
        <div className="section-title">Per-job overrides</div>
        <div className="config-hint">
          Pin a job to a driver, exclude it, allow top-off (any delivery),
          override a pickup's waste type, dump weight, or forced dump yard, set a
          desired service window (from–to), or force a job to follow another on the
          same truck. Default optimizes every job freely.
        </div>
        {jobs.length === 0 ? (
          <div className="no-jobs">No deliveries or pickups for this date.</div>
        ) : (
          <div className="jobs-list">
            {jobs.map((j) => (
              <div key={j.id} className="job-row">
                <span
                  className={`job-type-badge job-type-badge--${j.type}${
                    j.service === ServiceType.JUNK ? " job-type-badge--junk" : ""
                  }`}
                >
                  {j.type}
                </span>
                <span className="job-name">
                  {j.name}
                  {j.address ? ` — ${j.address}` : ""}
                </span>
                <select
                  className="job-mode"
                  value={jobMode[j.id] ?? "auto"}
                  onChange={(e) =>
                    setJobMode((m) => ({ ...m, [j.id]: e.target.value }))
                  }
                >
                  <option value="auto">Include</option>
                  <option value="exclude">Exclude</option>
                  {assignedDrivers.map((d) => (
                    <option key={d.id} value={d.id}>
                      Pin to {d.name}
                    </option>
                  ))}
                </select>
                {j.type === "delivery" && (
                  <label className="job-topoff">
                    <input
                      type="checkbox"
                      checked={!!topoff[j.id]}
                      onChange={(e) =>
                        setTopoff((t) => ({ ...t, [j.id]: e.target.checked }))
                      }
                    />
                    Top-off
                  </label>
                )}
                {j.type === "pickup" && (
                  <>
                    <select
                      className="job-waste"
                      title="Override this pickup's waste type for the run"
                      value={pickupWaste[j.id] ?? ""}
                      onChange={(e) =>
                        setPickupWaste((w) => ({ ...w, [j.id]: e.target.value }))
                      }
                    >
                      <option value="">Waste type</option>
                      {Object.values(WasteType).map((wt) => (
                        <option key={wt} value={wt}>
                          {wt}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      className="job-dump-weight"
                      title="Override billed dump weight (tons) for this pickup"
                      placeholder="avg t"
                      value={pickupDumpWeight[j.id] ?? ""}
                      onChange={(e) =>
                        setPickupDumpWeight((w) => ({ ...w, [j.id]: e.target.value }))
                      }
                    />
                    <select
                      className="job-dump-yard"
                      title="Force the dump yard for this pickup's container"
                      value={pickupDumpYard[j.id] ?? ""}
                      onChange={(e) =>
                        setPickupDumpYard((w) => ({ ...w, [j.id]: e.target.value }))
                      }
                    >
                      <option value="">Cheapest (auto)</option>
                      {dumpYards.map((dy) => (
                        <option key={dy.id} value={dy.id}>
                          {dy.name || dy.address}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <span className="job-window" title="Desired service window">
                  <input
                    type="time"
                    aria-label="Service window from"
                    value={timeWindow[j.id]?.start ?? ""}
                    onChange={(e) =>
                      setTimeWindow((w) => ({
                        ...w,
                        [j.id]: {
                          start: e.target.value,
                          end: w[j.id]?.end ?? "",
                        },
                      }))
                    }
                  />
                  <span className="job-window-dash">–</span>
                  <input
                    type="time"
                    aria-label="Service window to"
                    value={timeWindow[j.id]?.end ?? ""}
                    onChange={(e) =>
                      setTimeWindow((w) => ({
                        ...w,
                        [j.id]: {
                          start: w[j.id]?.start ?? "",
                          end: e.target.value,
                        },
                      }))
                    }
                  />
                </span>
                {j.type === "delivery" && (
                  <select
                    className="job-swap"
                    title="Same-box swap: return the customer's own box — this delivery must be the next stop after the chosen pickup, same truck"
                    aria-label={`Same-box swap, for ${j.name}`}
                    value={swapWith[j.id] ?? ""}
                    onChange={(e) =>
                      setSwapWith((m) => ({ ...m, [j.id]: e.target.value }))
                    }
                  >
                    <option value="">Same-box swap…</option>
                    {jobs
                      .filter((other) => other.type === "pickup" && other.id !== j.id)
                      .map((other) => (
                        <option key={other.id} value={other.id}>
                          returns pickup {other.name}
                          {other.address ? ` — ${other.address}` : ""}
                        </option>
                      ))}
                  </select>
                )}
                <select
                  className="job-must-follow"
                  title="Force this job to be serviced after another job, on the same truck"
                  aria-label={`Must follow, for ${j.name}`}
                  value={mustFollow[j.id] ?? ""}
                  onChange={(e) =>
                    setMustFollow((m) => ({ ...m, [j.id]: e.target.value }))
                  }
                >
                  <option value="">Must follow…</option>
                  {jobs
                    .filter((other) => other.id !== j.id)
                    .map((other) => (
                      <option key={other.id} value={other.id}>
                        after {other.type} {other.name}
                        {other.address ? ` — ${other.address}` : ""}
                      </option>
                    ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="form-actions">
        <button
          className="submit-button"
          onClick={onSubmit}
          disabled={!valid || createAPI.loading}
        >
          {createAPI.loading ? (
            <span className="submit-loading">
              <Oval color="#131313" height={16} width={16} secondaryColor="#FBD20A" />
              Running optimization…
            </span>
          ) : (
            "Run optimization"
          )}
        </button>
      </div>
    </div>
  );
}
